import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar } from 'lucide-react';

const DailyChart = ({ data, isLoading }) => {
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  const chartData = data?.daily_stats?.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    fullDate: item.date,
    value: item.ip_count,
  })) || [];

  const totalNew = chartData.reduce((sum, item) => sum + item.value, 0);
  const avgDaily = chartData.length > 0 ? Math.round(totalNew / chartData.length) : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border sharp shadow-xl p-3 font-mono">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
          <p className="text-lg font-bold text-primary tabular">
            {payload[0].value.toLocaleString()} IP
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="border border-border bg-card">
        <div className="px-5 py-3 border-b border-border bg-muted/30 animate-pulse">
          <div className="h-3 w-40 bg-muted" />
        </div>
        <div className="p-5">
          <div className="h-[350px] animate-pulse bg-muted/30" />
        </div>
      </div>
    );
  }

  const windowLabel = language === 'tr' ? 'SON 30 GÜN' : 'LAST 30 DAYS';
  const totalLabel = language === 'tr' ? 'TOPLAM' : 'TOTAL';
  const avgLabel = language === 'tr' ? 'GÜNLÜK ORT.' : 'DAILY AVG.';

  return (
    <div className="border border-border bg-card" data-testid="daily-chart">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
            {t('dailyStatistics')}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hidden sm:inline">
            // {t('newIPs')}
          </span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          {windowLabel}
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 border-b border-border">
        <div className="px-5 py-3 border-r border-border">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            {totalLabel}
          </div>
          <div className="mt-1 font-mono tabular text-2xl font-bold text-primary">
            {totalNew.toLocaleString()}
          </div>
        </div>
        <div className="px-5 py-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            {avgLabel}
          </div>
          <div className="mt-1 font-mono tabular text-2xl font-bold text-foreground">
            {avgDaily.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-5">
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke={theme === 'dark' ? '#1f2937' : '#e5e7eb'}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                dx={-10}
                tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '2 2' }} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={1.5}
                fillOpacity={1}
                fill="url(#colorValue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DailyChart;
